import { createSlice } from '@reduxjs/toolkit';

import type { NormalizedError } from 'errors/types';
import type { RootState } from 'state/store';

export type EnergyEstimate = {
  id: string;
  EAC: string | null;
};

export type TDCVBand = {
  High: number;
  Medium: number;
  Low: number;
};

export type EnergyEstimatesData = {
  elecEAC?: EnergyEstimate[] | null;
  gasEAC?: EnergyEstimate[] | null;
  TDCV?: {
    electricityUnrestricted: TDCVBand;
    electricityE7: TDCVBand;
    gas: TDCVBand;
  };
};

export type EnergyEstimatesState = {
  isLoading: boolean;
  hasRequested: boolean;
  data: EnergyEstimatesData | null;
  errors: NormalizedError[] | null;
  mpan: {
    [key: string]: string | null | undefined;
  };
  mprn: {
    [key: string]: string | null | undefined;
  };
};

type EnergyEstimatesLookup = {
  data: EnergyEstimatesData | null;
  mpan: EnergyEstimatesState['mpan'];
  mprn: EnergyEstimatesState['mprn'];
};

export const initialState: EnergyEstimatesState = {
  isLoading: false,
  hasRequested: false,
  data: null,
  errors: null,
  mpan: {},
  mprn: {}
};

export const slice = createSlice({
  name: 'energyEstimates',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.isLoading = initialState.isLoading;
      state.hasRequested = initialState.hasRequested;
      state.data = initialState.data;
      state.errors = initialState.errors;
      state.mpan = {};
      state.mprn = {};
    },
    setIsLoading: (state) => {
      state.isLoading = true;
      state.errors = initialState.errors;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.hasRequested = true;
      state.data = payload;
      state.errors = initialState.errors;
      state.mpan = {};
      state.mprn = {};
      const elecEAC = payload?.elecEAC ?? [];
      const gasEAC = payload?.gasEAC ?? [];

      if (elecEAC.length > 0) {
        elecEAC.forEach((estimate) => {
          state.mpan[estimate.id] = estimate.EAC;
        });
      }

      if (gasEAC.length > 0) {
        gasEAC.forEach((estimate) => {
          state.mprn[estimate.id] = estimate.EAC;
        });
      }
    },
    setError: (state, { payload }) => {
      state.isLoading = false;
      state.hasRequested = true;
      state.data = initialState.data;
      state.errors = payload;
      state.mpan = {};
      state.mprn = {};
    }
  }
});

export const {
  setInitialState,
  setIsLoading,
  setSuccess,
  setError
} = slice.actions;

export const selectSlice = (state: RootState) => state[slice.name];
export const selectIsLoading = (state: RootState) =>
  selectSlice(state).isLoading;
export const selectHasRequested = (state: RootState) =>
  selectSlice(state).hasRequested;
export const selectData = (state: RootState): EnergyEstimatesLookup => {
  const { data, mpan, mprn } = selectSlice(state);

  return {
    data,
    mpan,
    mprn
  };
};
export const selectErrors = (state: RootState) => selectSlice(state).errors;

export default slice.reducer;
